import { logger } from "@/utils/logger";
import { MessagePart } from './types';
import { decodeText } from '@/utils/textDecoding';
import { getVehicleCardByStyleIdSync } from '@/utils/showroomUtils';
import { ensureInventoryLoadedForHydration, getSessionInventory } from '@/utils/inventoryUtils';

/**
 * Check if images are accessible by attempting to load them
 * Returns a Set of accessible image URLs
 */
const checkImagesAccessibility = async (imageUrls: string[]): Promise<Set<string>> => {
  const accessibleImages = new Set<string>();
  
  const checkImage = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        img.src = '';
        resolve(false);
      }, 3000); // 3 second timeout
      
      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      img.src = url;
    });
  };
  
  const results = await Promise.all(
    imageUrls.map(async (url) => {
      const isAccessible = await checkImage(url);
      return { url, isAccessible };
    })
  );
  
  results.forEach(({ url, isAccessible }) => {
    if (isAccessible) {
      accessibleImages.add(url);
    }
  });
  
  return accessibleImages;
};

// Helper function to reorder images before links within numbered blocks
const reorderImagesBeforeLinks = (content: string): string => {
  // Split content by numbered items (1., 2., 3., etc.)
  const numberedBlockRegex = /(\d+\.\s+[^]*?)(?=\d+\.\s+|$)/g;
  const blocks = content.match(numberedBlockRegex);
  
  if (!blocks) return content;
  
  const reorderedBlocks = blocks.map(block => {
    // Extract images and links from the block
    const imageRegex = /<img[^>]*>/g;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    
    const images = [...block.matchAll(imageRegex)];
    const links = [...block.matchAll(linkRegex)];
    
    if (images.length === 0 || links.length === 0) return block;
    
    // Remove images and links from the block, keeping other content
    let cleanBlock = block.replace(imageRegex, '').replace(linkRegex, '').trim();
    
    // Reconstruct: clean content + images + links
    const imageHtml = images.map(match => match[0]).join(' ');
    const linkMarkdown = links.map(match => match[0]).join(' ');
    
    return `${cleanBlock} ${imageHtml} ${linkMarkdown}`;
  });
  
  return reorderedBlocks.join('\n\n');
};

// --- Vehicle Link Resolution (for {{styleid: X}} and {{vin: X}} patterns) ---

const detectLanguage = (): 'fr' | 'en' => {
  try {
    const metaTag = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement;
    if (metaTag && metaTag.content) {
      const langCode = metaTag.content.toLowerCase().split('-')[0];
      if (langCode === 'fr') return 'fr';
    }
  } catch {
    // Ignore errors
  }
  return 'en';
};

/** Normalize an object key: lowercase, strip spaces and underscores */
const normalizeKey = (key: string): string => key.toLowerCase().replace(/[\s_]/g, '');

const resolveStyleIdUrl = (styleId: string): string | null => {
  try {
    const vehicle = getVehicleCardByStyleIdSync(styleId);
    if (!vehicle) return null;
    
    const lang = detectLanguage();
    return lang === 'fr' ? vehicle.showroomUrlFrench : vehicle.showroomUrlEnglish;
  } catch {
    return null;
  }
};

const resolveVinUrl = (vin: string): string | null => {
  try {
    ensureInventoryLoadedForHydration();
    const inventory = getSessionInventory();
    if (!inventory || inventory.length === 0) return null;
    
    const vehicle = inventory.find(v => {
      const vehicleVin = v.VIN || v.vin || v['VIN Number'] || v.vinNumber;
      return vehicleVin && vehicleVin.toUpperCase() === vin.toUpperCase();
    });
    
    if (!vehicle) return null;
    
    let vdpUrl = vehicle['Vehicle Details Page (VDP)'] || 
                 vehicle['VDP URL'] || 
                 vehicle.VDP_URL || 
                 vehicle.vdpUrl || 
                 vehicle.vdp_url;
    
    if (!vdpUrl) return null;
    
    const lang = detectLanguage();
    if (lang === 'fr' && typeof vdpUrl === 'string') {
      // Handle /new/inventory/ first (more specific pattern)
      vdpUrl = vdpUrl.replace(/\/new\/inventory\//gi, '/neufs/inventaire/');
      // Then handle remaining patterns
      vdpUrl = vdpUrl.replace(/\/used\//gi, '/occasion/');
      vdpUrl = vdpUrl.replace(/\/new\//gi, '/neuf/');
    }
    
    return vdpUrl;
  } catch {
    return null;
  }
};

const isSameDomain = (url: string): boolean => {
  try {
    const linkUrl = new URL(url, window.location.origin);
    return linkUrl.hostname === window.location.hostname;
  } catch {
    return false;
  }
};

/**
 * Get vehicle display name from inventory by VIN
 */
const getVehicleNameByVin = (vin: string): string | null => {
  try {
    ensureInventoryLoadedForHydration();
    const inventory = getSessionInventory();
    
    if (!inventory || inventory.length === 0) return null;
    
    const vehicle = inventory.find(v => {
      const vehicleVin = v.VIN || v.vin || v['VIN Number'] || v.vinNumber;
      return vehicleVin && vehicleVin.toUpperCase() === vin.toUpperCase();
    });
    
    if (!vehicle) return null;
    
    const year = vehicle.Year || vehicle.year || '';
    const make = vehicle.Make || vehicle.make || '';
    const model = vehicle.Model || vehicle.model || '';
    let trim = vehicle.Trim || vehicle.trim || '';

    if (detectLanguage() === 'fr') {
      const frenchKey = Object.keys(vehicle).find(k => normalizeKey(k) === 'trimfrench');
      const frenchTrim = frenchKey ? vehicle[frenchKey] : undefined;
      if (typeof frenchTrim === 'string' && frenchTrim.trim()) {
        trim = frenchTrim;
      }
    }
    
    const parts = [year, make, model, trim].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  } catch {
    return null;
  }
};

/**
 * Get vehicle display name from showroom by StyleId
 */
const getVehicleNameByStyleId = (styleId: string): string | null => {
  try {
    const vehicle = getVehicleCardByStyleIdSync(styleId);
    if (!vehicle) return null;
    
    const year = vehicle.year || '';
    const make = vehicle.make || '';
    const model = vehicle.model || '';
    const trim = vehicle.trim || '';
    
    const parts = [year, make, model, trim].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  } catch {
    return null;
  }
};

const replaceVehicleLinksInText = (content: string): string => {
  const lang = detectLanguage();
  const linkText = lang === 'fr' ? 'Le voir ici' : 'View it here';
  
  // Replace {{styleid: X}} patterns
  let result = content.replace(/\{\{styleid:\s*([^}]+)\}\}/gi, (match, styleId) => {
    const trimmedId = styleId.trim();
    const url = resolveStyleIdUrl(trimmedId);
    const vehicleName = getVehicleNameByStyleId(trimmedId);
    
    if (url) {
      const displayText = vehicleName || linkText;
      const targetAttr = isSameDomain(url) ? '' : '{:target="_blank"}';
      return `[${displayText}](${url})${targetAttr}`;
    }
    
    // If no URL but have vehicle name, show as bold text
    if (vehicleName) {
      return `**${vehicleName}**`;
    }
    
    return ''; // Remove unresolved placeholders
  });
  
  // Replace {{vin: X}} patterns
  result = result.replace(/\{\{vin:\s*([^}]+)\}\}/gi, (match, vin) => {
    const trimmedVin = vin.trim();
    const url = resolveVinUrl(trimmedVin);
    const vehicleName = getVehicleNameByVin(trimmedVin);
    
    if (url) {
      const displayText = vehicleName || linkText;
      const targetAttr = isSameDomain(url) ? '' : '{:target="_blank"}';
      return `[${displayText}](${url})${targetAttr}`;
    }
    
    // If no URL but have vehicle name, show as bold text
    if (vehicleName) {
      return `**${vehicleName}**`;
    }
    
    return ''; // Remove unresolved placeholders
  });
  
  return result;
};

export const processIntro = async (intro: string, isSearchUrl: boolean = false): Promise<MessagePart[]> => {
  if (!intro) return [];
  
  logger.log('🎯 [processIntro] Original intro:', intro);
  // Detect language from HTML meta tag
  const detectLanguage = (): 'fr' | 'en' => {
    try {
      const metaTag = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement;
      if (metaTag && metaTag.content) {
        const contentLang = metaTag.content.toLowerCase();
        const langCode = contentLang.split('-')[0];
        if (langCode === 'fr') return 'fr';
      }
    } catch (error) {
      logger.warn('Failed to detect language from meta tag:', error);
    }
    return 'en';
  };
  
  // Get current page URL and domain for comparison
  const getCurrentPageUrl = () => {
    try {
      const stored = localStorage.getItem('context-current-page');
      const context = stored ? JSON.parse(stored) : null;
      return context?.url || '';
    } catch (error) {
      return '';
    }
  };

  const getCurrentDomain = () => {
    try {
      return window.location.hostname;
    } catch (error) {
      return '';
    }
  };

  const currentPageUrl = getCurrentPageUrl();
  const currentDomain = getCurrentDomain();
  const language = detectLanguage();
  
  // Fix encoding issues (é -> Ã©, etc.) FIRST before any processing
  let decodedIntro = decodeText(intro);
  
  // Replace vehicle link placeholders ({{styleid: X}} and {{vin: X}}) early
  decodedIntro = replaceVehicleLinksInText(decodedIntro);
  
  // Remove square bracketed strings containing .json (like 【7:3†showroom-index.json】)
  let cleanedIntro = decodedIntro.replace(/[【\[].*?\.json.*?[】\]]/g, '').trim();
  
  // Remove parentheses wrapping around links: (link) -> link, ([text](url)) -> [text](url)
  // Pattern 1: Remove parentheses around markdown links like ([text](url))
  cleanedIntro = cleanedIntro.replace(/\(\[([^\]]+)\]\(([^)]+)\)\)/g, '[$1]($2)');
  // Pattern 2: Remove parentheses around plain URLs like (https://...)
  cleanedIntro = cleanedIntro.replace(/\((https?:\/\/[^)\s]+)\)/g, '$1');
  
  // Detect and extract Google Maps links FIRST
  const googleMapsRegex = /(https:\/\/(?:www\.)?(?:maps\.google\.com|google\.com\/maps)[^\s<>"']+)/g;
  const googleMapsMatches = [...cleanedIntro.matchAll(googleMapsRegex)];
  
  if (googleMapsMatches.length > 0) {
    const mapsUrl = googleMapsMatches[0][1];
    // Remove the maps URL from the intro text
    let textWithoutMaps = cleanedIntro.replace(mapsUrl, '').trim();
    
    // Extract dealer name from [[...]] and address from [[[...]]]
    const dealerNameMatch = textWithoutMaps.match(/\[\[(.+?)\]\]/);
    const dealerAddressMatch = textWithoutMaps.match(/\[\[\[(.+?)\]\]\]/);
    
    const dealerName = dealerNameMatch ? dealerNameMatch[1].trim() : null;
    const dealerAddress = dealerAddressMatch ? dealerAddressMatch[1].trim() : null;
    
    // Remove the markers from text
    textWithoutMaps = textWithoutMaps.replace(/\[\[\[.+?\]\]\]/g, '').replace(/\[\[.+?\]\]/g, '');
    
    // Clean up extra whitespace
    textWithoutMaps = textWithoutMaps.replace(/\s+/g, ' ').trim();
    
    logger.log('*** Adding Intro with Google Maps link:', { text: textWithoutMaps, mapsUrl, dealerName, dealerAddress });
    return [{
      type: 'google_maps',
      content: {
        text: textWithoutMaps,
        mapsUrl: mapsUrl,
        dealerName,
        dealerAddress
      }
    }];
  }
  
  // Detect and process YouTube links INLINE
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?:[^\s]*)?/g;
  const youtubeMatches = [...cleanedIntro.matchAll(youtubeRegex)];
  
  if (youtubeMatches.length > 0) {
    const parts: MessagePart[] = [];
    let lastIndex = 0;
    
    youtubeMatches.forEach((match, index) => {
      const [fullMatch, videoId] = match;
      const matchStart = match.index!;
      
      // Add text before this video (if any)
      if (matchStart > lastIndex) {
        const textBefore = cleanedIntro.substring(lastIndex, matchStart).trim();
        if (textBefore) {
          parts.push({ type: 'text', content: textBefore });
        }
      }
      
      // Add the YouTube video with clean URL (no parameters)
      const videoUrl = `https://youtube.com/watch?v=${videoId}`;
      const buttonNumber = youtubeMatches.length > 1 ? ` #${index + 1}` : '';
      const buttonText = language === 'fr' ? `Voir vidéo${buttonNumber}` : `Watch Video${buttonNumber}`;
      
      parts.push({
        type: 'youtube_video',
        content: {
          url: videoUrl,
          buttonText: decodeText(buttonText)
        }
      });
      
      lastIndex = matchStart + fullMatch.length;
    });
    
    // Add any remaining text after the last video
    if (lastIndex < cleanedIntro.length) {
      const textAfter = cleanedIntro.substring(lastIndex).trim();
      if (textAfter) {
        parts.push({ type: 'text', content: textAfter });
      }
    }
    
    logger.log(`*** Processed Intro with inline YouTube videos: ${youtubeMatches.length} video(s)`);
    return parts;
  }
  
  // Check for plain d2cmedia CDN URLs and convert to full-width images
  const d2cImageUrlRegex = /(https:\/\/(?:carimages|imagescdn)\.d2cmedia\.ca\/[^\s<>"']+)/g;
  const d2cImageMatches = [...cleanedIntro.matchAll(d2cImageUrlRegex)];
  
  if (d2cImageMatches.length > 0) {
    let processedContent = cleanedIntro;
    d2cImageMatches.forEach(match => {
      const [fullMatch, imageUrl] = match;
      // Replace plain URL with full-width HTML img tag that respects container boundaries
      processedContent = processedContent.replace(fullMatch, `<img src="${imageUrl}" alt="Vehicle Image" class="max-w-full w-full rounded-lg my-2 block" />`);
    });
    
    // Clean up extra whitespace
    processedContent = processedContent.replace(/\s+/g, ' ').trim();
    
    logger.log('*** Adding Intro with D2C CDN URLs converted to images:', processedContent);
    return [{ type: 'text_with_links', content: processedContent }];
  }
  
  // Check for markdown image format like - ![EV6 Image](https://carimage.d2cmedia.ca/...)
  const imageRegex = /\s*-\s*!\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g;
  const imageMatches = [...cleanedIntro.matchAll(imageRegex)];
  
  if (imageMatches.length > 0) {
    // Replace markdown images (including the preceding dash) with HTML img tags
    let processedContent = cleanedIntro;
    imageMatches.forEach(match => {
      const [fullMatch, altText, imageUrl] = match;
      // Replace the "- ![...](...)" with just an img tag
      processedContent = processedContent.replace(fullMatch, `<img src="${imageUrl}" alt="${altText}" class="w-full max-w-md rounded-lg my-2" />`);
    });
    
    // Clean up extra whitespace and newlines
    processedContent = processedContent.replace(/\n\s*\n/g, '\n').replace(/\s+/g, ' ').trim();
    
    // Reorder content within numbered blocks to put images before links
    processedContent = reorderImagesBeforeLinks(processedContent);
    
    logger.log('*** Adding Intro with image processing:', processedContent);
    return [{ type: 'text_with_links', content: processedContent }];
  }
  
  // Check for image URLs (either with file extensions OR from known image CDN domains)
  const plainImageUrlRegex = /(https?:\/\/[^\s<>"']+\.(?:png|jpg|jpeg|webp|gif|svg)(?:\?[^\s<>"']*)?)/gi;
  const cdnImageUrlRegex = /(https?:\/\/(?:[^\/]*\.)?(?:scene7|cloudinary|imgix|akamaized|fastly|cloudflare)\.com\/[^\s<>"']+)/gi;
  
  const plainImageMatches = [...cleanedIntro.matchAll(plainImageUrlRegex)];
  const cdnImageMatches = [...cleanedIntro.matchAll(cdnImageUrlRegex)];
  const allImageMatches = [...plainImageMatches, ...cdnImageMatches];
  
  if (allImageMatches.length > 0) {
    // Check which images are actually accessible
    const imageUrls = [...new Set(allImageMatches.map(match => match[1] || match[0]))];
    const accessibleImages = await checkImagesAccessibility(imageUrls);
    
    let processedContent = cleanedIntro;
    allImageMatches.forEach(match => {
      const imageUrl = match[1] || match[0];
      if (accessibleImages.has(imageUrl)) {
        // Replace with rendered image
        processedContent = processedContent.replace(imageUrl, `<img src="${imageUrl}" alt="Image" class="max-w-full w-full rounded-lg my-2 block" />`);
      } else {
        // Remove inaccessible image URLs entirely
        processedContent = processedContent.replace(imageUrl, '');
        logger.log('*** Image not accessible, removing:', imageUrl);
      }
    });
    
    // Clean up extra whitespace
    processedContent = processedContent.replace(/\s+/g, ' ').trim();
    
    if (accessibleImages.size > 0) {
      logger.log('*** Adding Intro with verified image URLs:', processedContent);
      return [{ type: 'text_with_links', content: processedContent }];
    }
  }
  
  
  // If this is a search URL case, strip markdown links instead of converting them to buttons
  if (isSearchUrl) {
    cleanedIntro = cleanedIntro.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    logger.log('*** Adding Intro with links stripped (search URL case):', cleanedIntro);
    return [{ type: 'text', content: cleanedIntro }];
  }
  
  // Process markdown links to add target attribute for cross-domain links
  if (currentPageUrl || currentDomain) {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    cleanedIntro = cleanedIntro.replace(linkRegex, (match, linkText, linkUrl) => {
      // Remove links that match the current page URL
      if (linkUrl === currentPageUrl) {
        logger.log('*** Removing link that matches current page URL:', linkUrl);
        return linkText; // Just return the text without the link
      }
      
      // Check if the link is to a different domain
      try {
        const linkDomain = new URL(linkUrl).hostname;
        if (currentDomain && linkDomain !== currentDomain) {
          logger.log('*** Cross-domain link detected, will open in new tab:', linkUrl);
          // Add a special marker to indicate this should open in a new tab
          return `[${linkText}](${linkUrl}){:target="_blank"}`;
        }
      } catch (error) {
        // If URL parsing fails, treat as relative/same-domain link
        logger.log('*** Unable to parse link URL, treating as same-domain:', linkUrl);
      }
      
      return match; // Keep the original link for same-domain links
    });
  }
  
  logger.log('*** Adding Intro with link parsing:', cleanedIntro);
  logger.log('🎯 [processIntro] Final cleanedIntro with arrows:', cleanedIntro.substring(0, 300));
  return [{ type: 'text_with_links', content: cleanedIntro }];
};

export const processShowroom = (showroom: any, replyMessage?: any): MessagePart | null => {
  if (!showroom) return null;
  
  // Check if any inventory item has mileage > 1000, if so ignore showroom
  if (replyMessage?.Content?.Inventory && Array.isArray(replyMessage.Content.Inventory)) {
    const hasUsedVehicle = replyMessage.Content.Inventory.some((vehicle: any) => {
      const mileage = parseInt(vehicle.Mileage) || 0;
      return mileage > 1000;
    });
    
    if (hasUsedVehicle) {
      return null;
    }
  }
  
  return { type: 'showroom', content: showroom };
};

export const processClosing = (closing: string): MessagePart | null => {
  if (!closing) return null;
  
  // Fix encoding issues before processing
  const decodedClosing = decodeText(closing);
  
  logger.log('*** Adding Closing with link parsing:', decodedClosing);
  return { type: 'text_with_links', content: decodedClosing };
};

export const processSimilarVehicles = (
  similarVehicles: string | null, 
  proposedLink: string | null, 
  hasVehicles: boolean,
  ctaText?: string
): MessagePart | null => {
  const linkToUse = similarVehicles || proposedLink;
  if (!linkToUse) return null;
  
  return { 
    type: 'similar_vehicles', 
    content: { similar_vehicles: linkToUse, hasVehicles, cta_text: ctaText } 
  };
};

export const processWindowSticker = (windowStickerUrl: string): MessagePart | null => {
  if (!windowStickerUrl || typeof windowStickerUrl !== 'string' || !windowStickerUrl.trim()) {
    return null;
  }
  
  logger.log('*** Adding Window Sticker button:', windowStickerUrl);
  return { 
    type: 'text_with_links', 
    content: `[View Window Sticker](${windowStickerUrl.trim()}){:target="_blank"}` 
  };
};

export const processRootLevelProposedLink = (
  jsonData: any, 
  replyMessage: any
): MessagePart | null => {
  // Only process if no Similar_Vehicles or Proposed_Link in replyMessage
  if (!jsonData.Proposed_Link || replyMessage.Similar_Vehicles || replyMessage.Proposed_Link) {
    return null;
  }
  
  // Check if Proposed_Link already exists in Intro text
  if (replyMessage.Intro && replyMessage.Intro.includes(jsonData.Proposed_Link)) {
    logger.log('*** Proposed_Link already exists in Intro text, ignoring:', jsonData.Proposed_Link);
    return null;
  }
  
  logger.log('*** FOUND root-level Proposed_Link, adding to reply parts:', jsonData.Proposed_Link, 'with CTA:', jsonData.Proposed_Link_CTA);
  const hasVehicles = replyMessage.Content?.Inventory && Array.isArray(replyMessage.Content.Inventory) && replyMessage.Content.Inventory.length > 0;
  return { 
    type: 'similar_vehicles', 
    content: { similar_vehicles: jsonData.Proposed_Link, hasVehicles, cta_text: jsonData.Proposed_Link_CTA } 
  };
};